import { formatRelative } from '../api';
import type { NewsCheck } from '../types';
import { Badge } from './ui';

const VERDICT_TONE: Record<string, 'green' | 'red' | 'amber' | 'blue' | 'grey'> = {
  PROCEED: 'green',
  BLOCK: 'red',
  DELAY: 'amber',
  HOLD: 'blue',
  OVERRIDE: 'blue',
  FORCED: 'red',
  UNAVAILABLE: 'grey',
};

const VERDICT_LABEL: Record<string, string> = {
  PROCEED: 'news: go ahead',
  BLOCK: 'news: blocked',
  DELAY: 'news: delayed',
  HOLD: 'news: kept open',
  OVERRIDE: 'news: held through stop',
  FORCED: 'news: stop enforced',
  UNAVAILABLE: 'news: unavailable',
};

/** The news check behind a decision. Decoded from JSON, so every field is guarded. */
export default function NewsCheckLine({ check }: { check: NewsCheck }) {
  const verdict = check.verdict ?? 'UNAVAILABLE';
  const headlines = Array.isArray(check.headlines) ? check.headlines.slice(0, 3) : [];
  return (
    <div style={{ marginTop: 7, paddingLeft: 10, borderLeft: '2px solid var(--hair-strong)' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Badge tone={VERDICT_TONE[verdict] ?? 'grey'}>{VERDICT_LABEL[verdict] ?? verdict}</Badge>
        {check.materiality && <span className="mono-small">materiality {check.materiality}</span>}
        {check.tone && <span className="mono-small">{check.tone}</span>}
        {check.pendingEvent && <span className="mono-small">pending: {check.pendingEvent}</span>}
        {check.symbol && <span className="mono-small">news on {check.symbol}</span>}
      </div>
      {(check.summary || check.reason) && (
        <p className="reason" style={{ margin: '5px 0 0' }}>
          {check.summary}
          {check.summary && check.reason ? ' ' : ''}
          {check.reason && check.reason !== check.summary ? check.reason : ''}
        </p>
      )}
      {check.guard && (
        <p className="reason" style={{ margin: '4px 0 0', color: 'var(--amber)' }}>
          Limit applied: {check.guard}
        </p>
      )}
      {headlines.length > 0 && (
        <ul style={{ margin: '5px 0 0', paddingLeft: 16 }}>
          {headlines.map((headline, index) => (
            <li key={`${headline.title}-${index}`} className="mono-small" style={{ marginTop: 2 }}>
              {headline.url ? (
                <>
                  {/* Google News links (anything but a filing or announcement) go to a search,
                      not the opaque redirect, and say so. See News.tsx Headline. */}
                  <a href={headline.url} target="_blank" rel="noopener noreferrer nofollow">
                    {headline.title}
                  </a>
                  {headline.kind !== 'filing' && headline.kind !== 'announcement' && ' (via Google)'}
                </>
              ) : (
                headline.title
              )}{' '}
              · {headline.source} · {formatRelative(headline.publishedAt)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
